import { DatePicker, Input, PolicyDispatchingPermission, assets } from '@pulseops/common'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import PDTable from './PDTable'
import { ActivityIndicator, View } from 'react-native'
import { borderedDatePickerContainerStyle, borderedInputStyle } from '../styles/policyDispatchingStyles'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import PolicyDispatchingDialog from './PolicyDispatchingDialog'
import { formatDate, getPermissions } from '../utils/policyDispatchingUtils'
import { usePolicyDispatchingOffice } from './PolicyDispatchingOfficeContext'
import {
  AuthorizationLetterDeactivationPayload,
  AuthorizationLetterItem,
  PolicyDispatchingService
} from '../services/PolicyDispatchingService'

const headCell = [
  { id: 1, label: 'Ngưng sử dụng' },
  { id: 2, label: 'Mã số' },
  { id: 3, label: 'Ngày tạo' },
  { id: 4, label: 'Đại lý ủy quyền' },
  { id: 5, label: 'Người được ủy quyền' },
  { id: 6, label: 'Người phê duyệt' },
  { id: 7, label: 'Ngày có hiệu lực' },
  { id: 8, label: 'Ngày hết hiệu lực' }
]

const rowCell = [
  { id: 1, label: 'isPending' },
  { id: 2, label: 'id' },
  { id: 3, label: 'createDate' },
  { id: 4, label: 'authorizationAgent' },
  { id: 5, label: 'authorizedPerson' },
  { id: 6, label: 'approver' },
  { id: 7, label: 'effectiveDate' },
  { id: 8, label: 'expiryDate' }
]

type FormValues = {
  authorizationLetterId: string
  agentNumber: string
  agentGroup: string
  createdFromDate: Date | null
  createdToDate: Date | null
}

type AuthorizationLetterRow = AuthorizationLetterItem & {
  isPending: boolean
  createDate: string
  authorizationAgent: string
  effectiveDate: string
  expiryDate: string
  checked?: boolean
}

export default function PDManageAuthorizationLetter({ styles }: { styles: { [key: string]: any } }) {
  const { officeCode } = usePolicyDispatchingOffice()
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      authorizationLetterId: '',
      agentNumber: '',
      agentGroup: '',
      createdFromDate: null,
      createdToDate: null
    }
  })
  const [rows, setRows] = React.useState<AuthorizationLetterRow[]>([])
  const [selectedRows, setSelectedRows] = React.useState<AuthorizationLetterRow[]>([])
  const [allRowsSelected, setAllRowsSelected] = React.useState(false)
  const [pageInfo, setPageInfo] = React.useState<{
    page: number
    totalElements: number
    totalPages: number
    first: boolean
    last: boolean
  } | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)
  const lastSearchValues = React.useRef<FormValues | null>(null)
  const permissions = getPermissions()
  const allowDeactivate = permissions.includes(PolicyDispatchingPermission.AuthManagemementDeactivate)

  const toRow = (item: AuthorizationLetterItem): AuthorizationLetterRow => ({
    ...item,
    isPending: item.allowDeactivate,
    id: item.authorizationLetterId,
    createDate: item.createDate,
    authorizationAgent: item.agentNumber,
    effectiveDate: item.effectiveFromDate,
    expiryDate: item.effectiveToDate
  })

  const buildSearchPayload = (values: FormValues, page: number) => ({
    authorizationLetterId: values.authorizationLetterId || undefined,
    agentNumber: values.agentNumber || undefined,
    agentGroup: values.agentGroup || undefined,
    createdFromDate: formatDate(values.createdFromDate),
    createdToDate: formatDate(values.createdToDate),
    officeCode: officeCode || '',
    page
  })

  const search = (values: FormValues, page: number, resetSelection = false) => {
    if (!officeCode) {
      setModalInfo({ title: 'Văn phòng', message: 'Vui lòng chọn văn phòng trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    const hasSearchFilter = Boolean(
      values.authorizationLetterId ||
        values.agentNumber ||
        values.agentGroup ||
        values.createdFromDate ||
        values.createdToDate
    )
    if (!hasSearchFilter) {
      setModalInfo({ title: 'Tìm kiếm', message: 'Vui lòng nhập ít nhất một điều kiện tìm kiếm.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchAuthorizationLetters(buildSearchPayload(values, page)),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          if (!('body' in response)) {
            setRows([])
            setSelectedRows([])
            setAllRowsSelected(false)
            setPageInfo(null)
            setIsLoading(false)
            setModalInfo({ title: 'Tìm kiếm', message: 'Không tìm được thông tin theo yêu cầu' })
            setOpenModal(true)
            return
          }

          const preserveAllSelected = !resetSelection && allRowsSelected
          const selectedKeys = new Set((resetSelection ? [] : selectedRows).map((row) => row.authorizationLetterId))
          if (resetSelection) {
            setSelectedRows([])
            setAllRowsSelected(false)
          }
          const nextRows = response.body.letters.map((item) => ({
            ...toRow(item),
            checked: preserveAllSelected || selectedKeys.has(item.authorizationLetterId)
          }))
          setRows(nextRows)
          setPageInfo({
            page: response.body.page,
            totalElements: response.body.totalElements,
            totalPages: response.body.totalPages,
            first: response.body.page === 0,
            last: response.body.page >= response.body.totalPages - 1
          })
          setIsLoading(false)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setRows([])
          setSelectedRows([])
          setAllRowsSelected(false)
          setPageInfo(null)
          setIsLoading(false)
          setModalInfo({
            title: 'Tìm kiếm thất bại',
            message: 'Không thể tải danh sách giấy ủy quyền. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleSearch = handleSubmit((values) => {
    lastSearchValues.current = values
    search(values, 0, true)
  })

  const handlePageChange = (page: number) => {
    if (lastSearchValues.current) {
      search(lastSearchValues.current, page)
    }
  }

  const handleDeactivate = () => {
    if (selectedRows.length === 0) {
      setModalInfo({ title: 'Ngưng sử dụng', message: 'Vui lòng chọn ít nhất một giấy ủy quyền.' })
      setOpenModal(true)
      return
    }

    const canDeactivate = selectedRows.every((row) => row.allowDeactivate)
    if (!canDeactivate) {
      setModalInfo({
        title: 'Ngưng sử dụng',
        message: 'Chỉ có thể ngưng sử dụng các giấy ủy quyền đang ở trạng thái kích hoạt.'
      })
      setOpenModal(true)
      return
    }

    const payload: AuthorizationLetterDeactivationPayload[] = selectedRows.map((row) => ({
      authorizationLetterId: row.authorizationLetterId
    }))

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.deactivateAuthorizationLetters(payload),
      ZIO.tap(() =>
        ZIO.effectTotal(() => {
          setSelectedRows([])
          setAllRowsSelected(false)
          setIsLoading(false)
          if (lastSearchValues.current) {
            search(lastSearchValues.current, 0, true)
          }
          setModalInfo({
            title: 'Ngưng sử dụng',
            message: 'Ngưng sử dụng giấy ủy quyền thành công.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalInfo({
            title: 'Ngưng sử dụng',
            message: 'Không thể ngưng sử dụng giấy ủy quyền. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const getStyle = (value: boolean | string) => {
    if (value) {
      return {
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: '#008236',
        marginLeft: '20%'
      }
    } else {
      return {
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: '#fb2c36',
        marginLeft: '20%'
      }
    }
  }

  return (
    <View style={{ display: 'flex', flexDirection: 'column', gap: 24 } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="authorizationLetterId"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Số giấy ủy quyền'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
              />
            )}
          />
        </View>
        <View style={styles.column_1_2}>
          <Controller
            name="agentNumber"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Đại lý ủy quyền'}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
                maxLength={8}
              />
            )}
          />
        </View>
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_2}>
          <Controller
            name="agentGroup"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <Input
                title={'Có hiệu lực trên nhóm'}
                onChange={(e) => onChange(e.toUpperCase())}
                onBlur={onBlur}
                value={value}
                errorMessage={error?.message}
                inputStyle={borderedInputStyle}
                filled
              />
            )}
          />
        </View>
        <View style={styles.column_1_2} />
      </View>

      <View style={styles.row}>
        <View style={styles.column_1_4}>
          <Controller
            name="createdFromDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                alwaysShow
                label={'Tạo từ ngày'}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                maxDate={undefined}
                minDate={undefined}
                minDateMessage={''}
                errorMessage={error?.message}
                inputStyle={{ paddingBottom: 3 }}
                hideUnderline
                containerStyle={borderedDatePickerContainerStyle}
              />
            )}
          />
        </View>
        <View style={styles.column_1_4}>
          <Controller
            name="createdToDate"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <DatePicker
                alwaysShow
                label={'Đến ngày'}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                maxDate={undefined}
                minDate={undefined}
                minDateMessage={''}
                errorMessage={error?.message}
                inputStyle={{ paddingBottom: 3 }}
                hideUnderline
                containerStyle={borderedDatePickerContainerStyle}
              />
            )}
          />
        </View>
      </View>

      <View style={{ display: 'flex', flexDirection: 'row' } as any}>
        <button style={styles.submitButton} type="button" onClick={handleSearch} disabled={isLoading}>
          <span style={styles.mr_5}>
            <assets.SearchWhite style={{ color: '#fff' }} />
          </span>
          Tìm
        </button>
      </View>

      <View>
        <PDTable
          headCell={headCell}
          rowCell={rowCell}
          data={rows}
          hasCheckbox
          hasCheckAll
          getStyle={(cell, value) => (cell === 'isPending' ? getStyle(value) : {})}
          onSelectionChange={(nextRows, nextSelectedRows) => {
            setRows(nextRows)
            setSelectedRows((currentSelectedRows) => {
              const currentPageKeys = new Set(rows.map((row) => row.authorizationLetterId))
              const selectionsFromOtherPages = currentSelectedRows.filter(
                (row) => !currentPageKeys.has(row.authorizationLetterId)
              )
              const mergedSelectedRows = [...selectionsFromOtherPages, ...nextSelectedRows]

              setAllRowsSelected(
                Boolean(pageInfo && pageInfo.totalElements > 0 && mergedSelectedRows.length === pageInfo.totalElements)
              )

              return mergedSelectedRows
            })
          }}
          onSelectAllChange={(selected) => {
            setRows((currentRows: any[]) => currentRows.map((row: any) => ({ ...row, checked: selected })))

            if (!selected) {
              setAllRowsSelected(false)
              setSelectedRows([])
              return
            }

            if (!lastSearchValues.current || !pageInfo || pageInfo.totalPages <= 1) {
              setAllRowsSelected(true)
              setSelectedRows(rows)
              return
            }

            const values = lastSearchValues.current
            setIsLoading(true)
            Promise.all(
              Array.from({ length: pageInfo.totalPages }, (_, page) =>
                pipe(
                  PolicyDispatchingService.searchAuthorizationLetters(buildSearchPayload(values, page)),
                  ZIO.runPromise({})
                )
              )
            )
              .then((responses) => {
                const allRows = responses.flatMap((response) => {
                  const data = response as any
                  return 'body' in data
                    ? data.body.letters.map((item: AuthorizationLetterItem) => ({ ...toRow(item), checked: true }))
                    : []
                })
                setAllRowsSelected(true)
                setSelectedRows(allRows.length > 0 ? allRows : rows)
                setIsLoading(false)
              })
              .catch(() => {
                setSelectedRows(rows)
                setIsLoading(false)
                setModalInfo({
                  title: 'Chọn tất cả',
                  message: 'Không thể tải toàn bộ danh sách. Đã chọn các mục ở trang hiện tại.'
                })
                setOpenModal(true)
              })
          }}
          allRowsSelected={Boolean(allRowsSelected && pageInfo && pageInfo.totalElements > 0)}
          pagination={pageInfo ? { ...pageInfo, onPageChange: handlePageChange } : undefined}
        />
        <button
          style={{
            marginTop: 20,
            width: 'fit-content',
            ...(!allowDeactivate ? styles.disabledButton : styles.submitButton)
          }}
          type="button"
          onClick={handleDeactivate}
          disabled={isLoading || !allowDeactivate}
        >
          <span style={{ marginTop: 4, ...styles.mr_5 }}>
            <assets.PDPause style={{ color: '#fff' }} />
          </span>
          Ngưng sử dụng
        </button>
      </View>
    </View>
  )
}
